import { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Switch } from "@headlessui/react";
import { useNavigate } from 'react-router-dom';

export default function Numbers() {
  const [numbers, setNumbers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignModal, setAssignModal] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

  const [newNumber, setNewNumber] = useState({
    didNumber: '',
    callRate: '',
    country: '',
    expiryDate: '',
    mrc: ''
  });

  // Add country options array
  const countryOptions = [
    { value: "United States", label: "United States" },
    { value: "Australia", label: "Australia" },
    { value: "Canada", label: "Canada" }
  ];

  // Add console.log to debug users state
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchNumbers(), fetchUsers()]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch numbers from API
  const fetchNumbers = async () => {
    try {
      const token = localStorage.getItem('superadminToken');
      if (!token) {
        navigate('/superadmin/login');
        return;
      }

      const response = await fetch('https://didrouteapi.com/api/superadmin/numbers', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('superadminToken');
          localStorage.removeItem('superadminUser');
          navigate('/superadmin/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setNumbers(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch numbers');
      }
    } catch (err) {
      console.error('Error fetching numbers:', err);
      setError(err.message || 'Failed to fetch numbers');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setUsers(result.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle search
  const handleSearch = async (e) => {
    setSearchTerm(e.target.value);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/numbers/search?query=${e.target.value}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setNumbers(result.data);
      }
    } catch (error) {
      console.error('Error searching numbers:', error);
    }
  };

  const handleAssign = (number) => {
    setSelectedNumber(number);
    setAssignModal(true);
  };

  // Handle status toggle
  const handleStatusToggle = async (numberId) => {
    try {
      const token = localStorage.getItem('superadminToken');
      const response = await fetch(`${API_URL}/superadmin/numbers/${numberId}/toggle-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to toggle status');
      }
      
      await fetchNumbers(); // Refresh the list
      setSuccessMessage('Status updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling status:', error);
      setError(error.message);
    }
  };

  // Handle edit
  const handleEdit = (number) => {
    // Format the date to YYYY-MM-DD for the input field
    const formattedDate = number.expiry_date ? new Date(number.expiry_date).toISOString().split('T')[0] : '';
    
    setSelectedNumber({
      ...number,
      expiry_date: formattedDate // Use the formatted date
    });
    setSelectedUserEmail(number.user_email || '');
    setAssignModal(true);
  };

  // Handle delete
  const handleDelete = async (numberId) => {
    if (window.confirm('Are you sure you want to delete this number?')) {
      try {
        const token = localStorage.getItem('superadminToken');
        const response = await fetch(`${API_URL}/superadmin/numbers/${numberId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete number');
        }

        await fetchNumbers();
        setSuccessMessage('Number deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting number:', error);
        setError(error.message);
      }
    }
  };

  // Handle form submission
  const handleAssignSubmit = async () => {
    try {
      // Validate input first
      if (!selectedUserEmail) {
        setError('Please select a user');
        return;
      }

      const didNumber = selectedNumber ? selectedNumber.did_number : newNumber.didNumber;
      if (!didNumber) {
        setError('DID Number is required');
        return;
      }

      const token = localStorage.getItem('superadminToken');
      const method = selectedNumber ? 'PUT' : 'POST';
      const url = selectedNumber 
        ? `${API_URL}/superadmin/numbers/${selectedNumber.id}`
        : `${API_URL}/superadmin/numbers`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          didNumber: selectedNumber ? selectedNumber.did_number : newNumber.didNumber,
          duration: selectedNumber ? selectedNumber.call_rate : newNumber.callRate,
          country: selectedNumber ? selectedNumber.country : newNumber.country,
          expiryDate: selectedNumber ? selectedNumber.expiry_date : newNumber.expiryDate,
          userEmail: selectedUserEmail,
          mrc: selectedNumber ? selectedNumber.mrc : newNumber.mrc
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save number');
      }

      await fetchNumbers();
      setAssignModal(false);
      setSelectedNumber(null);
      setSelectedUserEmail('');
      setNewNumber({
        didNumber: '',
        callRate: '',
        country: '',
        expiryDate: '',
        mrc: ''
      });
      setSuccessMessage(selectedNumber ? 'Number updated successfully' : 'Number created successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving number:', error);
      setError(error.message);
      // Show error in modal and clear after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            {successMessage && (
              <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
                {successMessage}
              </div>
            )}

            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Numbers Management
                </h1>
              </div>

              <div className="mt-4 sm:mt-0 sm:flex sm:items-center sm:space-x-4">
                <input
                  type="text"
                  placeholder="Search numbers..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
                <button
                  onClick={() => setAssignModal(true)}
                  className="mt-4 sm:mt-0 px-4 py-2 whitespace-nowrap bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Assign Number
                </button>
              </div>
            </div>

            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-900">
                    <thead>
                      <tr>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black sm:pl-0">
                          DID Number
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Call Rate
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          MRC
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Country
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          User Email
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Expiry Date
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-800">
                      {numbers
                        .filter(
                          (number) =>
                            number.did_number
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            number.country
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            (number.user_email &&
                              number.user_email
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()))
                        )
                        .map((number) => (
                          <tr key={number.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                              {number.did_number}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {number.call_rate}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {number.mrc}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {number.country}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {number.user_email}
                            </td>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900">
                              {new Date(
                                number.expiry_date
                              ).toLocaleDateString()}
                            </td>
                            <td className="whitespace-nowr  ap py-4 px-3 text-xs">
                              <span
                                className={`px-3 py-1 rounded-full ${
                                  number.status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {number.status}
                              </span>
                            </td>
                            <td className="py-4 flex space-x-2 items-center justify-center">
                              {/* Status Toggle */}
                              <Switch
                                checked={number.status === "active"}
                                onChange={() => handleStatusToggle(number.id)}
                                className={`${
                                  number.status === "active"
                                    ? "bg-green-600"
                                    : "bg-gray-400"
                                } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                              >
                                <span
                                  className={`${
                                    number.status === "active"
                                      ? "translate-x-4"
                                      : "translate-x-1"
                                  } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                />
                              </Switch>
                              {/* Edit Button */}
                              <button
                                onClick={() => handleEdit(number)}
                                className="text-blue-400 hover:text-blue-500"
                                title="Edit"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              {/* Delete Button */}
                              <button
                                onClick={() => handleDelete(number.id)}
                                className="text-red-400 hover:text-red-500"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Updated Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedNumber ? "Edit Number" : "Add New Number"}
            </h2>

            {/* Add error message display */}
            {error && (
              <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            {/* User Email Field - Changed from select to input */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                User Email
              </label>
              <input
                type="email"
                value={selectedUserEmail}
                onChange={(e) => setSelectedUserEmail(e.target.value)}
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter user email"
              />
            </div>

            {/* DID Number Field */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                DID Number
              </label>
              <input
                type="text"
                value={
                  selectedNumber
                    ? selectedNumber.did_number
                    : newNumber.didNumber
                }
                onChange={(e) =>
                  selectedNumber
                    ? setSelectedNumber({
                        ...selectedNumber,
                        did_number: e.target.value,
                      })
                    : setNewNumber({ ...newNumber, didNumber: e.target.value })
                }
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter DID number"
              />
            </div>

            {/* Call Rate Field */}
            <div className="mb-2">
                <label className="block text-sm font-medium text-gray-900 mb-1">
                Call Rate
              </label>
              <input
                type="text"
                value={
                  selectedNumber ? selectedNumber.call_rate : newNumber.callRate
                }
                onChange={(e) =>
                  selectedNumber
                    ? setSelectedNumber({
                        ...selectedNumber,
                        call_rate: e.target.value,
                      })
                    : setNewNumber({ ...newNumber, callRate: e.target.value })
                }
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter call rate"
              />
            </div>

            {/* Expiry Date Field */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                value={
                  selectedNumber
                    ? selectedNumber.expiry_date
                    : newNumber.expiryDate
                }
                onChange={(e) =>
                  selectedNumber
                    ? setSelectedNumber({
                        ...selectedNumber,
                        expiry_date: e.target.value,
                      })
                    : setNewNumber({ ...newNumber, expiryDate: e.target.value })
                }
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            {/* Country Field */}
            <div className="mb-2">
                <label className="block text-sm font-medium text-gray-900 mb-1">
                Country
              </label>
              <select
                value={
                  selectedNumber ? selectedNumber.country : newNumber.country
                }
                onChange={(e) =>
                  selectedNumber
                    ? setSelectedNumber({
                        ...selectedNumber,
                        country: e.target.value,
                      })
                    : setNewNumber({ ...newNumber, country: e.target.value })
                }
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Select a country</option>
                {countryOptions.map((country) => (
                  <option
                    key={country.value}
                    value={country.value}
                    selected={
                      selectedNumber && selectedNumber.country === country.value
                    }
                  >
                    {country.label}
                  </option>
                ))}
              </select>
            </div>

            {/* MRC Field */}
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Monthly Recurring Charge
              </label>
              <input
                type="text"
                value={
                  selectedNumber ? selectedNumber.mrc : newNumber.mrc
                }
                onChange={(e) =>
                  selectedNumber
                    ? setSelectedNumber({
                        ...selectedNumber,
                        mrc: e.target.value,
                      })
                    : setNewNumber({ ...newNumber, mrc: e.target.value })
                }
                className="mt-1 block w-full px-2 py-1 bg-gray-200 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Enter monthly recurring charge"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setAssignModal(false);
                  setSelectedNumber(null);
                  setSelectedUserEmail("");
                  setNewNumber({
                    didNumber: "",
                    callRate: "",
                    country: "",
                    expiryDate: "",
                    mrc: ""
                  });
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                {selectedNumber ? "Save Changes" : "Add Number"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}